import React, { createContext, useContext, useState, useRef, useEffect, useMemo } from "react";
import { CheckoutType, ICartItemData, ICheckoutContext } from "@interfaces";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@redux";
import { fetchActiveDiscount } from "./checkout";
import _ from "lodash";
import { getItemsPriceInfo, transformCartItemToAddItemBase } from "./checkoutHelpers";
import { discountApi } from "src/Api/discountApi";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

interface CheckoutContextType {
    checkoutContext: ICheckoutContext;
    items: ICartItemData[];
    setItems: React.Dispatch<React.SetStateAction<ICartItemData[]>>;
    initialItems: ICartItemData[];
    isOrderDiscountLoading: boolean;
    activeDiscount?: number;
    orderDiscount?: number;
    originalPrice: number;
    discountedPrice?: number;
}

export const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

export const useCheckout = () => {
    const ctx = useContext(CheckoutContext);
    if (!ctx) throw new Error("useCheckout must be used within a CheckoutProvider");
    return ctx;
};

export const CheckoutProvider: React.FC<{
    initialItems: ICartItemData[];
    activeDiscount?: number;
    checkoutContext: ICheckoutContext;
    children: React.ReactNode;
}> = ({ initialItems: initialItemsProp, activeDiscount, checkoutContext, children }) => {
    const [items, setItems] = useState<ICartItemData[]>(initialItemsProp);
    const initialItems = useRef(initialItemsProp).current;
    const dispatch = useDispatch<AppDispatch>();
    const [orderDiscount, setOrderDiscount] = useState<number | undefined>();
    const [isOrderDiscountLoading, setIsOrderDiscountLoading] = useState(
        !!activeDiscount || (activeDiscount && activeDiscount > 0)
            ? true // if active discount is not available or greater than 0, order discount loading will be triggered below so show loading spinner
            : false,
    );

    // Synchronize internal state with parent updates
    // When initialItemsProp changes (e.g. cart updates from API),
    // reset local items state to match the new prop
    useEffect(() => {
        setItems(initialItemsProp);
    }, [initialItemsProp]);

    const originalPrice = useMemo(() => {
        return getItemsPriceInfo(items).totalPrice;
    }, [items]);

    const discountedPrice = useMemo(() => {
        return (orderDiscount ?? 0) > 0
            ? originalPrice - (originalPrice * (orderDiscount ?? 0)) / 100
            : undefined;
    }, [orderDiscount, originalPrice]);

    useEffect(() => {
        if (!activeDiscount) {
            dispatch(fetchActiveDiscount());
        }
    }, [activeDiscount]);

    useEffect(() => {
        const loadOrderDiscount = async () => {
            if (activeDiscount && activeDiscount > 0) {
                try {
                    setIsOrderDiscountLoading(true);
                    // if items are equal, get discount (cart/quote) and show it
                    // else send items, get discount using dry run and then show it
                    if (
                        !_.isEqual(initialItems, items) ||
                        checkoutContext.checkoutType === CheckoutType.BOOK_NOW
                    ) {
                        const updateItems = transformCartItemToAddItemBase(items);
                        const fetchedDiscountResp =
                            await discountApi.checkOrderDiscount(updateItems);
                        setOrderDiscount(fetchedDiscountResp.data.data.applicableDiscount);
                    } else if (checkoutContext.checkoutType === CheckoutType.CART) {
                        const fetchedCartDiscountResp = await discountApi.getCartDiscount();
                        setOrderDiscount(fetchedCartDiscountResp.data.data.applicableDiscount);
                    } else if (checkoutContext.checkoutType === CheckoutType.QUOTE) {
                        const fetchedQuoteDiscountResp = await discountApi.getQuoteDiscount(
                            checkoutContext.quoteId!,
                        );
                        setOrderDiscount(fetchedQuoteDiscountResp.data.data.applicableDiscount);
                    }
                } catch (error) {
                    console.error(error);
                    ErrorToast({ Message: `Failed to load order discount` });
                } finally {
                    setIsOrderDiscountLoading(false);
                }
            }
        };
        loadOrderDiscount();
    }, [activeDiscount, items]);

    return (
        <CheckoutContext.Provider
            value={{
                items,
                setItems,
                initialItems,
                checkoutContext,
                isOrderDiscountLoading,
                orderDiscount,
                discountedPrice,
                originalPrice,
                activeDiscount,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
};
